import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '../types';
import { api } from '../shared/api/apiClient';
import { SESSION_EXPIRED_EVENT } from '../shared/authSessionGuard';
import { hasCapability, hasRole, ROLES } from '../utils/accessControl';

interface MeResponse {
    id: number | string;
    username: string;
    email: string;
    roles: string[];
    isShopManager?: boolean;
}

const mapMe = (me: MeResponse): User => ({
    id: String(me.id),
    username: me.username,
    email: me.email,
    roles: me.roles || [],
    isShopManager: Boolean(me.isShopManager)
});

const persistUser = (user: User) => {
    localStorage.setItem('user', JSON.stringify(user));
};

export const useAuth = () => {
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);

    const applyUser = (user: User) => {
        persistUser(user);
        setCurrentUser(user);
        setIsAuthenticated(true);
    };

    const refreshCurrentUser = async (): Promise<User | null> => {
        try {
            const result = await api.get<MeResponse>('/api/auth/me', {}, false);
            if (!result.data) {
                return null;
            }

            const user = mapMe(result.data);
            applyUser(user);
            return user;
        } catch {
            return null;
        }
    };

    const navigateForUser = (user: User) => {
        if (hasRole(user, ROLES.admin)) {
            navigate('/users');
        } else if (hasCapability(user, 'integrations.access')) {
            navigate('/integrations');
        } else if (hasCapability(user, 'diagnostics.view')) {
            navigate('/diagnostics');
        } else if (user.isShopManager) {
            navigate('/shop-orders');
        } else {
            navigate('/products');
        }
    };

    const handleLogin = async (user: User) => {
        applyUser(user);
        setAuthLoading(false);

        const fresh = await refreshCurrentUser();
        navigateForUser(fresh ?? user);
    };

    const handleLogout = async () => {
        try {
            const response = await fetch('/api/auth/logout', {
                method: 'POST',
            });
            setCurrentUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('user');

            if (response.ok) {
                console.log('Successfully logged out');
            }
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        let cancelled = false;

        const checkAuthentication = async () => {
            try {
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const user = JSON.parse(userStr) as User;
                    if (!cancelled) {
                        setCurrentUser(user);
                        setIsAuthenticated(true);
                    }
                    try {
                        const result = await api.get<MeResponse>('/api/auth/me', {}, false);
                        if (!cancelled && result.data) {
                            const fresh = mapMe(result.data);
                            persistUser(fresh);
                            setCurrentUser(fresh);
                            setIsAuthenticated(true);
                        }
                    } catch {
                        // keep cached user; 401 handled by session guard
                    }
                }
            } catch (error) {
                console.error('Error parsing user data:', error);
                localStorage.removeItem('user');
            } finally {
                if (!cancelled) {
                    setAuthLoading(false);
                }
            }
        };

        checkAuthentication();

        const handleSessionExpired = () => {
            setCurrentUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('user');

            // Force navigation to login page even if React Router listeners are transiently not attached.
            window.location.replace('/');
        };

        window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

        return () => {
            cancelled = true;
            window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
        };
    }, []);

    return {
        currentUser,
        isAuthenticated,
        authLoading,
        handleLogin,
        handleLogout,
    };
};
